/* eslint-disable no-unused-vars */
/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Box } from '@chakra-ui/react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useParams } from 'react-router-dom';
import _ from 'lodash';
import { useFormValidationToast } from '../../../services/actionToast';
import { InputForm } from '../../inputs/forms/InputForm';
import { getFetchData, useFetch } from '../../../hooks/useFetch';
import { SelectInputForm } from '../../inputs/forms/SelectInputForm';
import { ImageInputForm } from '../../inputs/forms/ImageInputForm';
import { SwitchInputForm } from '../../inputs/forms/SwitchInputForm';
import { FormActions, FormRow, FormSection, FormShell } from '../../ui';
import { getFormattedLabel } from '../../../utils/FormatFunctions';
import { mapMultiSelectToRelation } from '../../../utils/formMappers';
import { useAuth } from '../../../providers/auth';

interface TutorFormProps {
  defaultValues?: any;
}

interface FormInputsProps {
  name: string;
  value: any;
  placeholder?: string;
  label: string;
  yup?: any;
}

export default function TutorForm({ defaultValues = {} }: TutorFormProps) {
  const validationToast = useFormValidationToast();
  const baseUrl = '/v1/admin/users';
  const { requestApi: reqApiTutors } = useFetch(baseUrl);
  const {
    requestApi: reqApiInstitutions,
    data: dataInstitutions,
    loading: loadingInstitutions,
  } = useFetch('/v1/admin/institutions');
  const { data: optionsInstitutions, pagination: paginationInstitutions } =
    getFetchData(dataInstitutions);
  const {
    requestApi: reqApiSecretaries,
    data: dataSecretaries,
    loading: loadingSecretaries,
  } = useFetch('/v1/admin/secretaries');
  const { data: optionsSecretaries, pagination: paginationSecretaries } =
    getFetchData(dataSecretaries);
  const {
    requestApi: reqApiModules,
    data: dataModules,
    loading: loadingModules,
  } = useFetch('/v1/admin/classes');
  const { data: optionsModules, pagination: paginationModules } = getFetchData(dataModules);
  const { itemId } = useParams();
  const navigate = useNavigate();
  const [images, setImages] = useState<any>('');
  const [uploading, setUploading] = useState<any>(false);
  const { user } = useAuth();
  const optionsGenre = [
    {
      label: 'Masculino',
      value: 'M',
    },
    {
      label: 'Feminino',
      value: 'F',
    },
  ];

  const yupschema: any = {};
  const defaultValuesSchema: any = {};

  const formInputs: FormInputsProps[] = [
    {
      name: 'name',
      value: defaultValues.name ?? '',
      placeholder: 'Nome do usuário',
      label: 'Nome',
      yup: Yup.string()
        .required('Este campo é obrigatório!')
        .min(3, 'Nome deve ter pelo menos 3 caracteres'),
    },
    {
      name: 'email',
      value: defaultValues.email ?? '',
      placeholder: 'usuario@email.com',
      label: 'Email',
      yup: Yup.string().email('Email inválido').required('Este campo é obrigatório!'),
    },
    {
      name: 'genre',
      value: defaultValues?.genre
        ? optionsGenre.find((item) => item.value === defaultValues?.genre)
        : null,
      placeholder: 'Gênero do usuário',
      label: 'Gênero',
      yup: Yup.mixed(),
    },
    {
      name: 'institutions',
      value: [
        ...(defaultValues?.institutions && defaultValues.institutions.length === 0
          ? [{ value: null, label: 'Todas' }]
          : []),
        ...(defaultValues?.institutions?.map((item) => getFormattedLabel(item)) ?? []),
      ],
      placeholder: 'Instituições do usuário',
      label: 'Instituições',
      yup: Yup.mixed(),
    },
    {
      name: 'school_classes',
      value: [
        ...(defaultValues?.school_classes && defaultValues.school_classes.length === 0
          ? [{ value: null, label: 'Todas' }]
          : []),
        ...(defaultValues?.school_classes?.map((item) => getFormattedLabel(item)) ?? []),
      ],
      placeholder: 'Selecione o módulo do tutor',
      label: 'Módulo',
      yup: Yup.mixed(),
    },
    {
      name: 'secretaries',
      value: [
        ...(defaultValues?.secretaries && defaultValues.secretaries.length === 0
          ? [{ value: null, label: 'Todas' }]
          : []),
        ...(defaultValues?.secretaries?.map((item) => getFormattedLabel(item)) ?? []),
      ],
      placeholder: 'Secretarias do tutor',
      label: 'Secretarias',
      yup: Yup.mixed(),
    },
    {
      name: 'image',
      value: defaultValues.image?.original ?? null,
      placeholder: '',
      label: 'Foto de perfil',
      yup: Yup.mixed().nullable(),
    },
    {
      name: 'status',
      value: defaultValues.status ?? true,
      label: 'Status',
      yup: Yup.boolean(),
    },
  ];

  formInputs.forEach((object) => {
    yupschema[object.name] = object.yup;
    defaultValuesSchema[object.name] = object.value;
  });
  const schema = Yup.object().shape(yupschema);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValuesSchema,
  });

  const name = formInputs.find((field) => field.name === 'name');
  const email = formInputs.find((field) => field.name === 'email');
  const genre = formInputs.find((field) => field.name === 'genre');
  const institutions = formInputs.find((field) => field.name === 'institutions');
  const module = formInputs.find((field) => field.name === 'school_classes');
  const secretaries = formInputs.find((field) => field.name === 'secretaries');
  const image = formInputs.find((field) => field.name === 'image');
  const status = formInputs.find((field) => field.name === 'status');

  const onSubmit = async (values: any) => {
    const shouldUseDefaultPassword = !defaultValues.name;
    const mappedInstitutions =
      user?.group?.id !== 7
        ? mapMultiSelectToRelation(values.institutions)
        : [{ id: user?.institution?.id }];
    const mappedSecretaries =
      user?.group?.id !== 7
        ? mapMultiSelectToRelation(values.secretaries)
        : [{ id: user?.institution?.secretary?.id }];
    const mappedSchoolClasses = mapMultiSelectToRelation(values.school_classes);

    const params = {
      ...values,
      group_id: 3,
      genre: values.genre?.value ?? null,
      use_default_password: shouldUseDefaultPassword,
      password: shouldUseDefaultPassword ? '123456' : _,
      confirm_password: _,
      institutions: mappedInstitutions,
      school_classes: mappedSchoolClasses,
      secretaries: mappedSecretaries,
      image: _,
    };
    const { status: resStatus, data } = await reqApiTutors(
      defaultValues.name ? 'put' : 'post',
      params,
      defaultValues.name ? `${baseUrl}/${itemId}` : null
    );
    if ((resStatus === 200 || resStatus === 201) && images) {
      setUploading(true);
      const apiUrlId = itemId ?? data?.data?.id;
      await reqApiTutors('put', { file: images.toString() }, `${baseUrl}/${apiUrlId}/images`);
      setUploading(false);
    }
    if (resStatus === 200 || resStatus === 201) navigate('/tutors');
  };

  useEffect(() => {
    if (defaultValues.name) {
      reset(defaultValuesSchema);
    }
  }, [defaultValues]);
  const isEdit = Boolean(defaultValues.name);
  const showScopeFields = user?.group?.id !== 7;

  return (
    <FormShell
      title={isEdit ? 'Editar Tutor' : 'Criar Tutor'}
      description={
        isEdit
          ? 'Atualize os dados e os vínculos deste tutor.'
          : 'Cadastre um tutor e defina a quais instituições, módulos e secretarias ele atende.'
      }
      backTo="/tutors"
      backLabel="Tutores"
    >
      <Box
        as="form"
        noValidate
        onSubmit={handleSubmit(onSubmit, validationToast)}
        display="flex"
        flexDirection="column"
        gap="16px"
      >
        <FormSection title="Informações básicas">
          <ImageInputForm
            variant="avatar"
            avatarName={watch('name')}
            defaultVal={defaultValues?.image}
            setImages={setImages}
            setIsUpping={setUploading}
            name={image?.name}
            label={image?.label}
            register={register}
            watch={watch}
            setValue={setValue}
            errors={errors}
          />
          <FormRow>
            <InputForm
              name={name?.name}
              label={name?.label}
              placeholder={name?.placeholder}
              isRequired
              register={register}
              errors={errors}
            />
            <InputForm
              name={email?.name}
              label={email?.label}
              placeholder={email?.placeholder}
              isRequired
              register={register}
              errors={errors}
            />
          </FormRow>
          <FormRow gridTemplateColumns={{ base: '1fr', md: '2fr 1fr' }}>
            <SelectInputForm
              name={genre?.name}
              label={genre?.label}
              options={optionsGenre}
              placeholder={genre?.placeholder}
              control={control}
              errors={errors}
            />
            <SwitchInputForm
              name={status?.name}
              label={status?.label}
              errors={errors}
              control={control}
            />
          </FormRow>
        </FormSection>

        <FormSection
          title="Vínculos"
          description="Defina o alcance de atuação do tutor. Deixe em branco ou selecione Todas para não restringir."
        >
          <FormRow>
            {showScopeFields && (
              <SelectInputForm
                name={institutions?.name}
                label={institutions?.label}
                options={[
                  { value: null, label: 'Todas' },
                  ...(optionsInstitutions?.map((item) => getFormattedLabel(item)) ?? []),
                ]}
                isLoading={loadingInstitutions}
                pagination={paginationInstitutions}
                getOptions={(filters) => reqApiInstitutions('get', { params: { ...filters } })}
                isMulti
                placeholder={institutions?.placeholder}
                control={control}
                errors={errors}
              />
            )}
            <SelectInputForm
              isMulti
              name={module?.name}
              label={module?.label}
              options={[
                { value: null, label: 'Todas' },
                ...(optionsModules?.map((item) => getFormattedLabel(item)) ?? []),
              ]}
              isLoading={loadingModules}
              pagination={paginationModules}
              getOptions={(filters: any) => reqApiModules('get', { params: { ...filters } })}
              placeholder={module?.placeholder}
              control={control}
              errors={errors}
            />
            {showScopeFields && (
              <SelectInputForm
                name={secretaries?.name}
                label={secretaries?.label}
                options={[
                  { value: null, label: 'Todas' },
                  ...(optionsSecretaries?.map((item) => getFormattedLabel(item)) ?? []),
                ]}
                isLoading={loadingSecretaries}
                pagination={paginationSecretaries}
                getOptions={(filters) => reqApiSecretaries('get', { params: { ...filters } })}
                isMulti
                placeholder={secretaries?.placeholder}
                control={control}
                errors={errors}
              />
            )}
          </FormRow>
        </FormSection>

        <FormActions cancelTo="/tutors" isLoading={isSubmitting || uploading} />
      </Box>
    </FormShell>
  );
}
